import {useState} from 'react';
import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {useAuth} from '../hooks/useAuth';

const PRIMARY_COLOR = '#3ab3ff';

export function LoginPage() {
    const t = useTraduire();
    const {login, loginWithEmail, loading, error} = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showEmailForm, setShowEmailForm] = useState(false);

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (email && password) {
            await loginWithEmail(email, password);
        }
    };

  return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0f0f0]">
          <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-bo">
        <div className="text-center">
            <img src="/assimil-logo.svg" alt="Assimil" className="h-8 mx-auto mb-4"/>
            <h1 className="text-2xl font-semibold text-gray-900">{t(T.titreConnexion)}</h1>
            <p className="mt-2 text-sm text-gray-500">{t(T.sousTitreConnexion)}</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

          <div className="space-y-4">
              {/* Google Sign In */}
              <button
                  onClick={login}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-pill shadow-bo bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                  </svg>
                  {loading ? 'Signing in...' : 'Sign in with Google'}
              </button>

              {/* Divider */}
              <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"/>
                  </div>
                  <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-gray-50 text-gray-500">or</span>
                  </div>
              </div>

              {/* Email/Password Toggle */}
              {!showEmailForm ? (
                  <button
                      onClick={() => setShowEmailForm(true)}
                      className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-pill shadow-bo bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                  >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      Sign in with Email
                  </button>
              ) : (
                  <form onSubmit={handleEmailSubmit} className="space-y-4">
                      <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                              Email
                          </label>
                          <input
                              id="email"
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent"
                              style={{'--tw-ring-color': PRIMARY_COLOR} as React.CSSProperties}
                              placeholder="you@example.com"
                          />
                      </div>
                      <div>
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                              Password
                          </label>
                          <input
                              id="password"
                              type="password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              required
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent"
                              style={{'--tw-ring-color': PRIMARY_COLOR} as React.CSSProperties}
                              placeholder="••••••••"
                          />
                      </div>
                      <button
                          type="submit"
                          disabled={loading || !email || !password}
                          className="w-full px-4 py-3 text-white rounded-pill shadow-bo hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                          style={{backgroundColor: PRIMARY_COLOR}}
                      >
                          {loading ? t(T.connexionEnCours) : t(T.seConnecter)}
                      </button>
                      <button
                          type="button"
                          onClick={() => setShowEmailForm(false)}
                          className="w-full text-sm text-gray-500 hover:text-gray-700"
                      >
                          Back to other options
                      </button>
                  </form>
              )}
          </div>

        <p className="text-center text-sm text-gray-500">
          Only authorized administrators can access this panel.
        </p>
      </div>
    </div>
  );
}
